import { useMemo, useState } from 'react';
import { FeeTierDisplayUnit } from 'src/components/Modals/FeeTierModal/types';
import {
	FEE_TIER_DISPLAY_UNIT_OPTIONS,
	VOLUME_TIERS,
} from 'src/components/Modals/FeeTierModal/consts';
import useFeeTierTable from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModalTable';
import useUserFeeTier from 'src/components/Modals/FeeTierModal/hooks/useUserFeeTier';
import useTakerFeeGroups from 'src/components/Modals/FeeTierModal/hooks/useTakerFeeGroups';
import usePerpFeeTiers from 'src/components/Modals/FeeTierModal/hooks/usePerpFeeTiers';
import useFeeTierHandlers from 'src/components/Modals/FeeTierModal/hooks/useFeeTierModalHandlers';
import getNextVolumeTierIndex from 'src/components/Modals/FeeTierModal/nextVolumeTier';
import { formatText, PRESETS } from '@velocity-exchange/common/format';

/**
 * View state for the fee table modal: the tier ladder and its rates, where the
 * user sits on it, and their progress toward the next rung.
 *
 * The rung a user is charged at and the rung their volume has earned can differ
 * while a promo runs (see `useUserFeeTier`), so the two drive different parts of
 * the modal: the charged tier highlights a row and sets which rung is worth
 * chasing next, while the volume readout stays what the user has actually
 * traded.
 */
export default function useFeeTierModal() {
	const [displayUnit, setDisplayUnit] = useState<FeeTierDisplayUnit>(
		FEE_TIER_DISPLAY_UNIT_OPTIONS[0].value
	);

	const {
		userVolumeTierIndex,
		userFeeTierIndex,
		isPromoLiftingUserTier,
		userCurrent30dVolume,
		isLoadingVolume,
	} = useUserFeeTier();

	const feeTiers = usePerpFeeTiers();

	// Measured from the tier being charged, so a promo floor does not leave the
	// user chasing a rung they are already beating.
	const userNextTierIndex = getNextVolumeTierIndex(
		userVolumeTierIndex,
		userFeeTierIndex
	);
	const showNextVolumeTier = userNextTierIndex !== undefined;

	const takerFeeGroups = useTakerFeeGroups();

	const { rows } = useFeeTierTable({
		feeTiers,
		takerFeeGroups,
		displayUnit,
	});

	const { handleChangeDisplayUnit, handleCloseModal } = useFeeTierHandlers({
		setDisplayUnit,
	});

	const userCurrentVolume = isLoadingVolume
		? '-'
		: formatText(userCurrent30dVolume, PRESETS.usd);

	const userNextTier =
		userNextTierIndex === undefined
			? undefined
			: VOLUME_TIERS[userNextTierIndex];
	const userNextTierVolume = userNextTier?.display;
	const userNextTierLabel = userNextTier?.title;

	const progressBarPercentage = useMemo(() => {
		// Maxed out when there is no next tier to show, whether the user reached
		// the top rung on volume or a promo put them there.
		if (userNextTierIndex === undefined) {
			return 100;
		}

		const current = userCurrent30dVolume?.toNum() ?? 0;
		const total = VOLUME_TIERS[userNextTierIndex]?.amount ?? 0;

		if (!total) {
			return 0;
		}

		const percentage = (current / total) * 100;

		// We use `99` as the actual max otherwise the progress bar thumb styling will be off.
		return Math.min(Math.max(percentage, 0), 99);
	}, [userCurrent30dVolume, userNextTierIndex]);

	return {
		feeTiers,
		userFeeTierIndex,
		isPromoLiftingUserTier,
		userCurrentVolume,
		userNextTierVolume,
		userNextTierLabel,
		showNextVolumeTier,
		takerFeeGroups,
		displayUnit,
		tableRows: rows,
		progressBarPercentage,
		handleChangeDisplayUnit,
		handleCloseModal,
	};
}
